/**
 * Argentine provinces and localities (public/geo/ar, built by scripts/build-geo.mjs from the official
 * Georef data, datos.gob.ar, CC BY 4.0). Loaded on demand and cached for the session.
 */
import { useEffect, useState } from "react";

export interface Province {
  id: string;
  name: string;
}
/** [locality, department (partido / comuna)] */
export type Locality = [string, string];

let provincesCache: Promise<Province[]> | null = null;
const localityCache = new Map<string, Promise<Locality[]>>();

const load = <T,>(path: string): Promise<T> =>
  fetch(path).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json() as Promise<T>;
  });

export function loadProvinces(): Promise<Province[]> {
  provincesCache ??= load<Province[]>("/geo/ar/provinces.json").catch((err) => {
    provincesCache = null;
    throw err;
  });
  return provincesCache;
}

export function loadLocalities(provinceId: string): Promise<Locality[]> {
  if (!/^\d{2}$/.test(provinceId)) return Promise.resolve([]);
  let p = localityCache.get(provinceId);
  if (!p) {
    p = load<Locality[]>(`/geo/ar/${provinceId}.json`).catch((err) => {
      localityCache.delete(provinceId);
      throw err;
    });
    localityCache.set(provinceId, p);
  }
  return p;
}

/** Provinces, and the localities of the province named `provinceName` (by name, as stored). */
export function useGeo(provinceName: string): { provinces: Province[]; province: Province | null; localities: Locality[] } {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [localities, setLocalities] = useState<Locality[]>([]);
  useEffect(() => {
    loadProvinces().then(setProvinces).catch(() => setProvinces([]));
  }, []);
  const province = findProvince(provinces, provinceName);
  const provinceId = province?.id ?? "";
  useEffect(() => {
    let alive = true;
    setLocalities([]);
    if (provinceId) loadLocalities(provinceId).then((l) => alive && setLocalities(l)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [provinceId]);
  return { provinces, province, localities };
}

const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Matches stored names loosely ("CABA", "Capital Federal", accents…). */
export function findProvince(provinces: Province[], name: string | null | undefined): Province | null {
  const n = fold(name ?? "");
  if (!n) return null;
  if (["caba", "capital federal", "ciudad de buenos aires", "ciudad autonoma de buenos aires"].includes(n)) return provinces.find((p) => p.id === "02") ?? null;
  return provinces.find((p) => fold(p.name) === n) ?? null;
}
